const REMOVED_FLAG = '___removed';

export function isFunction(obj: any): obj is Function {
    return {}.toString.apply(obj) === '[object Function]';
};

export function isArray(obj: any): obj is Array<any> {
    return {}.toString.apply(obj) === '[object Array]';
};

export function isDate(obj: any): obj is Date {
    return {}.toString.apply(obj) === '[object Date]';
};

export function isObject(obj: any): obj is Object {
    return {}.toString.apply(obj) === '[object Object]';
}

export function isValue(obj: any) {
    return !isObject(obj) && !isArray(obj);
}

export function isString(obj: any): obj is string {
    return typeof obj === 'string' || obj instanceof String;
}

export function isUndefined(obj: any) {
    return typeof obj === 'undefined';
}

export interface IObjectsDiff extends Object {
}

export function getObjectsDiff(orig: Object, current: Object): IObjectsDiff {
    const propertyPath: string[] = [];
    const diff: IObjectsDiff = {};
    _getObjectsDiff(orig, current, diff, propertyPath);
    return diff;
}

function _getObjectsDiff(orig: Object, current: Object, diff: IObjectsDiff, propertyPath: string[]) {

    if (isFunction(orig) || isFunction(current)) {
        throw 'Invalid argument. Function given, object expected.';
    }
    if (isValue(orig) || isValue(current)) {
        throw 'Invalid argument. Value given, object expected.';
    }

    propertyPath = shallowCopy(propertyPath);

    for (let propertyName in orig) {
        const currentPropertyPath = [...propertyPath, propertyName];

        let origValue = orig[propertyName];
        let currentValue = current[propertyName];

        if (isUndefined(currentValue)) {
            addToDiff(diff, currentPropertyPath, REMOVED_FLAG);
            continue;
        }

        if (typeof (origValue) !== typeof (currentValue)) {
            addToDiff(diff, currentPropertyPath, currentValue);
        }

        if (isObject(origValue) || isArray(origValue)) {
            _getObjectsDiff(origValue, currentValue, diff, currentPropertyPath);
            continue;
        }

        if (isDate(origValue)) {
            origValue = origValue.getTime();
            currentValue = currentValue.getTime();
        }

        if (origValue !== currentValue) {
            addToDiff(diff, currentPropertyPath, currentValue);
        }
    }

    for (let propertyName in current) {
        if (isUndefined(orig[propertyName])) {
            addToDiff(diff, [...propertyPath, propertyName], current[propertyName]);
        }
    }
}

/*function replacePropertyPathSeparatorInPropertyName(propertyName: string): string {
    return propertyName.replace(new RegExp('\\' + PROPERTY_PATH_SEPARATOR, 'g'), PROPERTY_PATH_SEPARATOR_IN_PROPERTY_NAME_PLACEHOLDER);
}

function returnPropertyPathSeparatorToPropertyName(propertyName: string): string {
    return propertyName.replace(new RegExp('\\' + PROPERTY_PATH_SEPARATOR_IN_PROPERTY_NAME_PLACEHOLDER, 'g'), PROPERTY_PATH_SEPARATOR);
}*/

/*function addToDiff(diff: IObjectsDiff, propertyPath: string[], value: any) {
    propertyPath[propertyPath.length - 1] = replacePropertyPathSeparatorInPropertyName(propertyPath[propertyPath.length - 1]);
    diff[propertyPath.join(PROPERTY_PATH_SEPARATOR)] = value;
}*/

function addToDiff(diff: IObjectsDiff, propertyPath: string[], value: any) {
    for (let i = 0; i < propertyPath.length; i++) {
        const propertyName = propertyPath[i];
        if (i == propertyPath.length - 1) {
            diff[propertyName] = value;
            return;
        }
        diff = diff[propertyName] || (diff[propertyName] = {});
    }
}

export function applyObjectsDiff(obj: any, diff: IObjectsDiff, ignoreRemoved: boolean = false) {
    for (let propertyName in diff) {
        const diffValue = diff[propertyName];
        let objValue = obj[propertyName];
        if (diffValue === REMOVED_FLAG) {
            if (!ignoreRemoved) {
                delete obj[propertyName];
            }
            continue;
        }
        if (typeof (objValue) !== typeof (diffValue) || isValue(diffValue)) {
            objValue = obj[propertyName] = diffValue;
        }
        if (!isValue(diffValue)) {
            applyObjectsDiff(objValue, diffValue, ignoreRemoved);
        }
    }
}

export function getObjectPropertyValue(o: Object, dotSeparatedPropertyPath: string): any {
    if (!o) return undefined;
    const propertyPath = dotSeparatedPropertyPath.split('.');
    const propertyPathLength = propertyPath.length;
    for (let i = 0; i < propertyPathLength; i++) {
        const propertyName = propertyPath[i];
        if (i == propertyPathLength - 1) {
            return o[propertyName];
        }
        else {
            o = o[propertyName];
            if (isUndefined(o)) return undefined;
        }
    }
}

export function setObjectPropertyValue(o: Object, dotSeparatedPropertyPath: string, value: any): Object {
    const _o = o;
    const propertyPath = dotSeparatedPropertyPath.split('.');
    const propertyPathLength = propertyPath.length;
    for (let i = 0; i < propertyPathLength; i++) {
        const propertyName = propertyPath[i];
        if (i == propertyPathLength - 1) {
            o[propertyName] = value;
            break;
        }
        o = o[propertyName] || (o[propertyName] = {});
    }
    return _o;
}

export function forEachObjectProperty(o: Object, propertyModificationLogic: (propertyValue: any) => any) {
    for (let propertyName in o) {
        const propertyValue = o[propertyName];
        if (isObject(propertyValue)) {
            forEachObjectProperty(propertyValue, propertyModificationLogic);
            continue;
        }
        o[propertyName] = propertyModificationLogic(propertyValue);
    }
}

export function findObjectWithPropertyValue(rootObject: Object, propertyName: string, propertyValue: any): string[] | null {
    const search = (path: string[], obj: Object): string[] | null => {
        for (let propName in obj) {
            if (propName === propertyName && obj[propName] === propertyValue) {
                return path;
            }
            if (typeof obj[propName] === 'object') {
                const result = search([...path, propName], obj[propName]);
                if (result) return result;
            }
        }
        return null;
    };
    return search([], rootObject);
}

export function shallowCopy<T>(source: T, callback?: (target: T) => void | T): T;
export function shallowCopy<T>(source: T[], callback?: (target: T[]) => void | T[]): T[];
export function shallowCopy(source: any, callback?: (target: any) => void | any): any {
    if (source instanceof Array) {
        source = [...source];
        const result = callback ? callback(source) : undefined;
        return result || source;
    }
    return objectShallowCopy(source, callback);
}

export function objectShallowCopy<T>(source: T, callback: (target: T) => void | T = (_t: T) => { }): T {
    const target = <T>{};
    for (var property in source)
        if (source.hasOwnProperty(property))
            (<any>target)[property] = (<any>source)[property];

    const result = callback(target);
    return <T>result || target;
};

export function deepFreeze<T extends Object>(source: T): T {
    Object.freeze(source);
    for (var property in source) {
        const sourceProperty = (<any>source)[property];
        if (source.hasOwnProperty(property)
            && sourceProperty !== null
            && (typeof sourceProperty === "object" || typeof sourceProperty === "function")
            && !Object.isFrozen(sourceProperty))
            deepFreeze(sourceProperty);
    }

    return source;
};

// export function deepCopy<T>(source: T, callback?: (target: T) => void): T;
// export function deepCopy<T>(source: T[], callback?: (target: T[]) => void): T[];
// export function deepCopy(source: any, callback?: (target: any) => void): any {
//     let target = source;
//     if (source && typeof source === 'object') {
//         target = Object.prototype.toString.call(source) === "[object Array]" ? [] : {};
//         for (var i in source) {
//             target[i] = deepCopy(source[i]);
//         }
//     }
//     return callback ? callback(target) : target;
// }

export function deepCopy<T>(source: T, callback: (target: T) => void | T = (_t: T) => { }): T {
    const target = {} as T;

    // TODO: remove and create more complex deepcopy but now is necessary for layout editor in settings form 
    // Note: cache should not be re-used by repeated calls to JSON.stringify.
    var cache: any[] = [];
    const str = JSON.stringify(source, function (_key, value) {
        if (typeof value === 'object' && value !== null) {
            if (cache.indexOf(value) !== -1) {
                // Circular reference found, discard key
                return;
            }
            // Store value in our collection
            cache.push(value);
        }
        return value;
    });

    const sourceCopy = JSON.parse(str);
    for (let property in source)
        if (sourceCopy.hasOwnProperty(property))
            target[property] = sourceCopy[property];
    const result = callback(target);
    return (result as T) || target;
}
